import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import Footer from "../components/Footer";
import "../programacion/programacion.css";

type Moment = "Apertura" | "Charlas" | "Networking" | "Cierre";

interface Photo {
  id: string;
  src: string;
  alt: string;
  moment: Moment;
}

const PHOTOS: Photo[] = [
  { id: "01", src: "/images/galeria/01.jpg", alt: "Bienvenida Open Space", moment: "Apertura" },
  { id: "02", src: "/images/galeria/02.jpg", alt: "Sala llena en apertura", moment: "Apertura" },
  { id: "03", src: "/images/galeria/03.jpg", alt: "Dinámica de grupo", moment: "Apertura" },
  { id: "04", src: "/images/galeria/04.jpg", alt: "Charla en Factory", moment: "Charlas" },
  { id: "05", src: "/images/galeria/05.jpg", alt: "Ponente en escena", moment: "Charlas" },
  { id: "06", src: "/images/galeria/06.jpg", alt: "Debate entre asistentes", moment: "Charlas" },
  { id: "07", src: "/images/galeria/07.jpg", alt: "Networking en pasillo", moment: "Networking" },
  { id: "08", src: "/images/galeria/08.jpg", alt: "Café y conversación", moment: "Networking" },
  { id: "09", src: "/images/galeria/09.jpg", alt: "Grupo en terraza", moment: "Networking" },
  { id: "10", src: "/images/galeria/10.jpg", alt: "Ronda de preguntas", moment: "Charlas" },
  { id: "11", src: "/images/galeria/11.jpg", alt: "Cierre colectivo", moment: "Cierre" },
  { id: "12", src: "/images/galeria/12.jpg", alt: "Aplausos finales", moment: "Cierre" },
  { id: "13", src: "/images/galeria/13.jpg", alt: "Foto de grupo", moment: "Cierre" },
  { id: "14", src: "/images/galeria/14.jpg", alt: "Último brindis", moment: "Cierre" },
];

const MOMENTS: Moment[] = ["Apertura", "Charlas", "Networking", "Cierre"];
const FILTERS: Array<Moment | "Todas"> = ["Todas", ...MOMENTS];
const SLUG: Record<Moment, string> = {
  Apertura: "apertura",
  Charlas: "charlas",
  Networking: "networking",
  Cierre: "cierre",
};
const SWIPE = 80;

const coverOf = (moment: Moment) => PHOTOS.find((p) => p.moment === moment)!;

const Galeria = () => {
  const [filter, setFilter] = useState<(typeof FILTERS)[number]>("Todas");
  const [openId, setOpenId] = useState<string | null>(null);
  const [lbDrag, setLbDrag] = useState({ x: 0, y: 0, active: false });
  const [leaving, setLeaving] = useState<Photo | null>(null);
  const leaveTimer = useRef<number>();
  const [slide, setSlide] = useState(0);
  const sliderRef = useRef<HTMLDivElement>(null);
  const thumbsRef = useRef<HTMLDivElement>(null);
  const startRef = useRef({ x: 0, y: 0 });
  const reduced = usePrefersReducedMotion();

  const visible = useMemo(
    () => (filter === "Todas" ? PHOTOS : PHOTOS.filter((p) => p.moment === filter)),
    [filter]
  );

  const openIndex = openId ? visible.findIndex((p) => p.id === openId) : -1;
  const openPhoto = openIndex >= 0 ? visible[openIndex] : null;

  const swapTo = (id: string) => {
    if (openPhoto && openPhoto.id !== id && !reduced) {
      setLeaving(openPhoto);
      window.clearTimeout(leaveTimer.current);
      leaveTimer.current = window.setTimeout(() => setLeaving(null), 700);
    }
    setOpenId(id);
    setLbDrag({ x: 0, y: 0, active: false });
  };

  useEffect(() => {
    if (openIndex < 0) return;
    [visible[openIndex - 1], visible[openIndex + 1]].forEach((p) => {
      if (!p) return;
      const img = new Image();
      img.src = p.src;
    });
  }, [openIndex, visible]);

  const goSlide = (i: number) => {
    const next = (i + MOMENTS.length) % MOMENTS.length;
    setSlide(next);
    const el = sliderRef.current;
    if (!el) return;
    el.scrollTo({ left: next * el.clientWidth, behavior: reduced ? "auto" : "smooth" });
  };

  useEffect(() => {
    const el = sliderRef.current;
    if (!el) return;
    const onScroll = () => {
      const i = Math.round(el.scrollLeft / el.clientWidth);
      setSlide(i);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (reduced) return;
    const id = window.setInterval(() => goSlide(slide + 1), 4800);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slide, reduced]);

  useEffect(() => {
    const root = thumbsRef.current;
    if (!root || !openPhoto) return;
    const active = root.querySelector<HTMLElement>('[aria-current="true"]');
    if (!active) return;
    const pad = 12;
    const left = active.getBoundingClientRect().left - root.getBoundingClientRect().left + root.scrollLeft;
    const right = left + active.offsetWidth;
    const max = root.scrollWidth - root.clientWidth;
    let next = root.scrollLeft;
    if (left < next + pad) next = left - pad;
    else if (right > next + root.clientWidth - pad) next = right - root.clientWidth + pad;
    next = Math.max(0, Math.min(next, max));
    if (Math.abs(next - root.scrollLeft) > 1) {
      root.scrollTo({ left: next, behavior: reduced ? "auto" : "smooth" });
    }
  }, [openPhoto, reduced]);

  useEffect(() => {
    if (!openPhoto) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpenId(null);
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openPhoto]);

  const go = (delta: number) => {
    if (openIndex < 0 || !visible.length) return;
    const next = (openIndex + delta + visible.length) % visible.length;
    swapTo(visible[next].id);
  };

  const openAt = (id: string) => swapTo(id);

  const onLbPointerDown = (e: React.PointerEvent) => {
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    startRef.current = { x: e.clientX, y: e.clientY };
    setLbDrag({ x: 0, y: 0, active: true });
  };
  const onLbPointerMove = (e: React.PointerEvent) => {
    if (!lbDrag.active) return;
    setLbDrag({
      x: e.clientX - startRef.current.x,
      y: e.clientY - startRef.current.y,
      active: true,
    });
  };
  const onLbPointerUp = () => {
    if (!lbDrag.active) return;
    if (lbDrag.y > 110) setOpenId(null);
    else if (Math.abs(lbDrag.x) > SWIPE) go(lbDrag.x < 0 ? 1 : -1);
    setLbDrag({ x: 0, y: 0, active: false });
  };

  const goToSection = (moment: Moment) => {
    setFilter(moment);
    requestAnimationFrame(() => {
      document.getElementById("galeria-fotos")?.scrollIntoView({
        behavior: reduced ? "auto" : "smooth",
        block: "start",
      });
    });
  };

  return (
    <div className="w-full min-h-screen flex flex-col items-center text-white">
      <div className="c-decor-hex c-decor-hex--header" aria-hidden="true" />
      <div className="c-decor-hex c-decor-hex--galeria" aria-hidden="true" />
      <section className="relative z-10 w-full" aria-label="Secciones de la galería">
        <div
          ref={sliderRef}
          className="flex overflow-x-auto snap-x snap-mandatory no-scrollbar h-[62dvh] min-h-[320px]"
        >
          {MOMENTS.map((moment) => {
            const cover = coverOf(moment);
            return (
              <a
                key={moment}
                href={`#${SLUG[moment]}`}
                onClick={(e) => {
                  e.preventDefault();
                  goToSection(moment);
                }}
                className="relative shrink-0 w-full h-full snap-center overflow-hidden focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-4px] focus-visible:outline-white"
              >
                <img
                  src={cover.src}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover ken"
                  style={{
                    animation: reduced ? undefined : "kenBurns 18s ease-in-out infinite",
                  }}
                />
                <span className="absolute inset-0 bg-gradient-to-t from-jakala-dark-blue via-jakala-dark-blue/35 to-black/20" />
                <span className="absolute inset-x-0 bottom-0 px-6 pb-14 pt-20">
                  <span className="block text-jakala-red text-xs font-bold tracking-[0.28em] uppercase mb-2">
                    Sección
                  </span>
                  <span className="block text-5xl font-extrabold leading-none tracking-tight">
                    {moment}
                  </span>
                </span>
              </a>
            );
          })}
        </div>
        <div className="absolute bottom-4 left-0 right-0 z-10 flex justify-center gap-2">
          {MOMENTS.map((moment, i) => (
            <button
              key={moment}
              type="button"
              aria-label={moment}
              aria-current={slide === i}
              onClick={() => goSlide(i)}
              className={`h-2 rounded-full transition-all ${
                slide === i ? "w-6 bg-jakala-red" : "w-2 bg-white/40"
              }`}
            />
          ))}
        </div>
      </section>

      <div className="relative w-full max-w-[1400px] 3xl:max-w-[1920px] px-8">
      <div className="sticky top-0 z-30 mt-10 bg-jakala-dark-blue/90 backdrop-blur-md border-b border-white/10">
        <div className="flex gap-2 overflow-x-auto px-4 py-3 no-scrollbar snap-x">
          {FILTERS.map((m) => {
            const on = filter === m;
            return (
              <button
                key={m}
                type="button"
                onClick={() => setFilter(m)}
                className={`snap-start shrink-0 min-h-11 px-4 rounded-full text-sm font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white ${
                  on ? "bg-jakala-red text-white" : "bg-white/10 text-white/80"
                }`}
              >
                {m}
              </button>
            );
          })}
        </div>
      </div>

      <section id="galeria-fotos" className="scroll-mt-16 px-2 py-2">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
          {visible.map((photo) => (
            <button
              key={photo.id}
              type="button"
              onClick={() => setOpenId(photo.id)}
              className="relative aspect-square overflow-hidden rounded-2xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            >
              <img
                src={photo.src}
                alt={photo.alt}
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      </section>

      <div className="pt-10">
        <Footer />
      </div>
      </div>

      {openPhoto && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={openPhoto.alt}
          className="fixed inset-0 z-[80] bg-jakala-dark-blue flex flex-col overflow-hidden"
          style={{
            paddingTop: "env(safe-area-inset-top)",
            paddingBottom: "env(safe-area-inset-bottom)",
            opacity: lbDrag.active ? Math.max(0.4, 1 - lbDrag.y / 280) : 1,
          }}
        >
          <div className="c-decor-hex c-decor-hex--visor" aria-hidden="true" />

          <header className="relative z-10 flex items-center justify-end gap-3 px-4 md:px-10 pt-5 pb-3 max-md:pt-3 max-md:pb-10 shrink-0">
            <div className="mr-auto min-w-0">
              <p className="text-jakala-red text-[11px] font-bold tracking-[0.28em] uppercase">
                Sección
              </p>
              <h2 className="text-2xl font-extrabold leading-none tracking-tight truncate">
                {openPhoto.moment}
              </h2>
            </div>
            <button
              type="button"
              onClick={() => setOpenId(null)}
              className="min-w-11 min-h-11 rounded-full bg-jakala-red flex items-center justify-center text-white hover:bg-jakala-dark-red transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              aria-label="Cerrar"
            >
              <X size={22} strokeWidth={2.5} />
            </button>
          </header>

          <div className="relative z-10 flex-1 flex flex-col min-h-0 justify-start max-md:gap-8 max-md:px-4 max-md:pb-[max(0.75rem,env(safe-area-inset-bottom))] md:justify-start">
            <div className="flex items-start justify-center min-h-0 md:flex-1 md:items-center md:px-6">
            <div className="flex items-center gap-10 md:gap-16 w-full max-w-[min(100%,1100px)]">
            <button
              type="button"
              onClick={() => go(-1)}
              onPointerDown={(e) => e.stopPropagation()}
              className="hidden md:flex z-20 shrink-0 min-w-12 min-h-12 rounded-full outline outline-1 outline-white/40 [outline-style:dashed] items-center justify-center text-white hover:bg-white/10 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
              aria-label="Anterior"
            >
              <ChevronLeft />
            </button>
            <div
              className="flex flex-col items-center flex-1 min-w-0 touch-none"
              onPointerDown={onLbPointerDown}
              onPointerMove={onLbPointerMove}
              onPointerUp={onLbPointerUp}
              onPointerCancel={onLbPointerUp}
              style={{
                transform: lbDrag.active
                  ? `translate(${lbDrag.x}px, ${Math.max(0, lbDrag.y)}px)`
                  : undefined,
              }}
            >
              <figure className="relative m-0 w-full h-auto md:h-[min(62dvh,720px)]">
                <img
                  src={openPhoto.src}
                  alt={openPhoto.alt}
                  className="w-full h-auto max-h-[calc(100dvh-16rem)] object-contain rounded-xl pointer-events-none md:absolute md:inset-0 md:h-full md:max-h-none"
                />
                {leaving && !reduced && (
                  <img
                    key={leaving.id}
                    src={leaving.src}
                    alt=""
                    aria-hidden
                    className="absolute inset-0 w-full h-full object-contain rounded-xl pointer-events-none"
                    style={{ animation: "lbOut 700ms ease forwards" }}
                  />
                )}
              </figure>
              <div className="flex md:hidden items-center justify-center gap-5 mt-8">
                <button
                  type="button"
                  onClick={() => go(-1)}
                  onPointerDown={(e) => e.stopPropagation()}
                  className="min-w-11 min-h-11 rounded-full outline outline-1 outline-white/40 [outline-style:dashed] flex items-center justify-center"
                  aria-label="Anterior"
                >
                  <ChevronLeft />
                </button>
                <button
                  type="button"
                  onClick={() => go(1)}
                  onPointerDown={(e) => e.stopPropagation()}
                  className="min-w-11 min-h-11 rounded-full outline outline-1 outline-white/40 [outline-style:dashed] flex items-center justify-center"
                  aria-label="Siguiente"
                >
                  <ChevronRight />
                </button>
              </div>
              <p className="text-white/60 text-sm font-semibold mt-4 md:mt-5">
                {String(openIndex + 1).padStart(2, "0")} / {String(visible.length).padStart(2, "0")}
              </p>
            </div>
            <button
              type="button"
              onClick={() => go(1)}
              onPointerDown={(e) => e.stopPropagation()}
              className="hidden md:flex z-20 shrink-0 min-w-12 min-h-12 rounded-full outline outline-1 outline-white/40 [outline-style:dashed] items-center justify-center text-white hover:bg-white/10 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
              aria-label="Siguiente"
            >
              <ChevronRight />
            </button>
            </div>
            </div>

          <footer className="relative shrink-0 pt-2 md:px-10 md:pb-5 md:pt-3">
            <p className="sr-only">Miniaturas de la galería</p>
            <div ref={thumbsRef} className="overflow-x-auto no-scrollbar">
            <div className="flex justify-center gap-2 w-max min-w-full py-2">
              {visible.map((photo) => {
                const on = photo.id === openPhoto.id;
                return (
                  <button
                    key={photo.id}
                    type="button"
                    onClick={() => openAt(photo.id)}
                    aria-current={on}
                    aria-label={photo.alt}
                    className={`shrink-0 w-14 h-14 md:w-16 md:h-16 rounded-lg overflow-hidden transition-opacity focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white ${
                      on
                        ? "outline outline-2 outline-offset-2 outline-jakala-red opacity-100"
                        : "opacity-45 hover:opacity-80"
                    }`}
                  >
                    <img src={photo.src} alt="" className="w-full h-full object-cover" />
                  </button>
                );
              })}
            </div>
            </div>
          </footer>
          </div>
        </div>
      )}

      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { scrollbar-width: none; }
        @keyframes lbOut {
          from { opacity: 1; }
          to { opacity: 0; }
        }
        @keyframes kenBurns {
          0% { transform: scale(1) translate(0, 0); }
          50% { transform: scale(1.14) translate(-2%, -1%); }
          100% { transform: scale(1) translate(0, 0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .ken { animation: none !important; }
        }
      `}</style>
    </div>
  );
};

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReduced(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);
  return reduced;
}

export default Galeria;
